/**
 * InventarioView -- NUCLEO de la vista de inventario.
 *
 * SEP-INV-T2 (mision ARQ-SEPARACION-V1-001): esta vista tenia 3.615 lineas y viajaba
 * entera en el bundle principal. Aqui quedan solo el armazon (montaje, cabecera de
 * operador, estado de sync, router de pestanas, banner de sin-conexion y bloqueo de
 * operador) y la pestana Informacion, que son 14 lineas y no merecia archivo propio.
 *
 * Las 8 pestanas viven en ./inventario/ y se cargan bajo demanda. Los helpers que
 * comparten estan en ./inventario/core.js.
 *
 * Es una MUDANZA: ningun bloque cambio de logica. Foto del comportamiento previo en
 * SEP-FOTO-T1 (Argy-Q), que es el criterio de exito.
 *
 * Audiencia: el colaborador en la tablet del negocio (verificacion, entrada, avance)
 * y el dueno o administrativo en escritorio (configuracion, historial, informacion,
 * productos, insumos).
 */
import { ApiBase } from '../../infrastructure/api/ApiBase.js';
import { getSelectedBusiness } from '../components/BusinessSelector.js';
import { getOperador } from '../components/OperadorPin.js';
import { OfflineQueue } from '../../infrastructure/offline/OfflineQueue.js';
import { attachPullToRefresh } from '../shared/pullToRefresh.js';
import { setPageTitle } from '../components/NavBar.js';
import { isBusinessUser, esc, fetchSyncStatus, escOp } from './inventario/core.js';
import { AppConfig } from '../../config.js';
import { puedeGestionar } from '../../application/compras/gestionMatches.js';
import { regresoHtml } from '../shared/regreso.js';
import { formatearFecha } from '../shared/fechaColombiana.js';

let currentTab = 'productos';


export async function renderInventario(container) {
  // Tab por defecto: "verificacion" (principal segun PANEL-OPS-DASHBOARDS-001 T2)
  currentTab = 'verificacion';
  const bizId = getSelectedBusiness() || '';
  const esNegocio = isBusinessUser();

  // Titulo en navbar (visible solo en role-client via CSS).
  setPageTitle(esNegocio ? 'Verificacion de Inventario' : '');

  // Restriccion para cuentas de negocio: requiere operador identificado (PIN).
  // Si no hay operador, mostramos bloqueo grande hasta que alguien se identifique.
  // Escucha "operador:changed" para re-render automatico tras ingreso de PIN.
  if (esNegocio && !getOperador()) {
    renderBloqueoOperador(container);
    // KIOSK-FIX (bit #2977): guard por ruta — este listener {once} quedaba vivo al navegar
    // a otra vista (p.ej. #/argemiro-kiosk) y al confirmar PIN alla re-renderizaba
    // Inventario ENCIMA de la vista activa. Solo re-render si seguimos en #/inventario.
    const onOp = () => {
      if (!location.hash.startsWith('#/inventario')) return;
      if (getOperador()) renderInventario(container);
    };
    window.addEventListener('operador:changed', onOp, { once: true });
    return;
  }

  // V2 ROUND 2 (bit #1308) T3: version visible en kiosk para troubleshoot futuro.
  // __APP_VERSION__ definido por vite.config.js desde package.json.
  const appVersion = (typeof __APP_VERSION__ !== 'undefined') ? __APP_VERSION__ : 'dev';

  // FRONT-VISTAS-EC-V04-T1: la cabecera que ve quien cuenta, a 14px. Por AMBITO (los ids de esta vista): .btn-filter y
  // .ops-operator-chip son compartidas y otras vistas no cambian.
  container.innerHTML = `
    <style>#nx-inv-tabs .btn-filter,#nx-inv-op-header .ops-operator-chip,#nx-inv-content .banco-stat-label{font-size:14px}</style>
    <section class="artifact-view">
      <div class="artifact-header" style="display:flex;justify-content:space-between;align-items:flex-start;gap:10px;flex-wrap:wrap">
        <div>
          ${regresoHtml()}
          <h1>Inventario <span style="font-size:0.875rem;color:var(--text-faint);font-weight:400;margin-left:6px;vertical-align:middle">v${esc(appVersion)}</span></h1>
          <p style="font-size:14px;color:var(--text-secondary);margin-top:2px">Datos sincronizados de Nexo Desktop. Verificacion de stock con asistencia del agente.</p>
        </div>
        <div id="nx-inv-op-header"></div>
      </div>

      <div id="nx-inv-offline-banner"></div>

      <div class="task-filter-bar" id="nx-inv-tabs">
        <button class="btn-filter active" data-inv-tab="verificacion">Verificacion</button>
        <button class="btn-filter" data-inv-tab="entrada">Entrada</button>
        <button class="btn-filter" data-inv-tab="avance">Avance grupos</button>
        <button class="btn-filter" data-inv-tab="historial">Historial</button>
        <button class="btn-filter" data-inv-tab="productos">Productos</button>
        <button class="btn-filter" data-inv-tab="insumos">Insumos</button>
        ${puedeGestionar(AppConfig.role) ? '<button class="btn-filter" data-inv-tab="matches">Matches de facturas</button>' : ''}
        <button class="btn-filter" data-inv-tab="configuracion">Configuracion</button>
        <button class="btn-filter" data-inv-tab="informacion">Informacion</button>
      </div>

      <div id="nx-inv-content" style="margin-top:12px"></div>
    </section>`;

  renderInvOperatorHeader();
  window.addEventListener('operador:changed', renderInvOperatorHeader);
  renderInvOfflineBanner();
  window.addEventListener('online',  renderInvOfflineBanner);
  window.addEventListener('offline', renderInvOfflineBanner);
  window.addEventListener('nexo-offline-queue-change', renderInvOfflineBanner);
  // FIX #2050: drenar al montar la vista (antes solo CarritoView drenaba; si el
  // operador solo usa el kiosk, lo encolado quedaba pendiente para siempre).
  flushInventarioPendientes().catch(() => {});

  const $tabs = document.getElementById('nx-inv-tabs');
  const $content = document.getElementById('nx-inv-content');

  $tabs.addEventListener('click', (e) => {
    const btn = e.target.closest('.btn-filter');
    if (!btn) return;
    $tabs.querySelectorAll('.btn-filter').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    currentTab = btn.dataset.invTab;
    renderTab($content, currentTab, bizId);
  });

  renderTab($content, currentTab, bizId);

  // PANEL-OPS-V3-001 T3 — pull-to-refresh (solo touch devices)
  attachPullToRefresh($content, async () => {
    await renderTab($content, currentTab, bizId);
  });
}



function renderInvOperatorHeader() {
  const $el = document.getElementById('nx-inv-op-header');
  if (!$el) return;
  const op = getOperador();
  $el.innerHTML = op
    ? `<button type="button" class="ops-operator-chip" id="nx-inv-op-btn" title="Cambiar operador">
         <span>&#128100;</span><span>${escOp(op.nombre)}</span><span style="opacity:0.6">(cambiar)</span>
       </button>`
    : `<button type="button" class="ops-operator-chip ops-operator-chip--empty" id="nx-inv-op-btn" title="Identificar operador">
         <span>&#128275;</span><span>Identificar operador</span>
       </button>`;
  document.getElementById('nx-inv-op-btn')?.addEventListener('click', () => {
    const kioskBtn = document.getElementById('nx-op-badge') || document.getElementById('nx-op-identify');
    if (kioskBtn) kioskBtn.click();
  });
}



async function loadSyncStatus(bizId) {
  const el = document.getElementById('nx-inv-sync-status');
  if (!el) return;
  const res = await fetchSyncStatus(bizId);
  if (!res.ok) {
    el.innerHTML = `<div class="banco-stat-card" style="border-color:var(--accent-yellow)"><span style="color:var(--accent-yellow);font-size:0.875rem">No se pudo cargar estado de sync. Verifica que la API este activa.</span></div>`;
    return;
  }
  const tables = res.data?.data || [];
  const synced = tables.filter(t => t.count > 0);
  const total = tables.reduce((s, t) => s + (t.count || 0), 0);
  const lastSync = synced.length > 0
    ? synced.map(t => t.lastSyncAt).filter(Boolean).sort().reverse()[0]
    : null;
  const lastStr = lastSync ? formatearFecha(lastSync, { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' }, { parte: 'todo' }) : 'nunca';

  // Calcular frescura
  let freshBadge = '';
  if (lastSync) {
    const hoursAgo = Math.floor((Date.now() - new Date(lastSync)) / 3600000);
    const freshClass = hoursAgo < 24 ? 'da-badge-ok' : hoursAgo < 72 ? 'da-badge-yellow' : 'da-badge-red';
    const freshLabel = hoursAgo < 1 ? 'Sincronizado hace menos de 1h' : hoursAgo < 24 ? `Sincronizado hace ${hoursAgo}h` : `Sincronizado hace ${Math.floor(hoursAgo / 24)} dias`;
    freshBadge = `<span class="da-badge ${freshClass}" style="font-size:0.875rem">${freshLabel}</span>`;
  } else {
    freshBadge = `<span class="da-badge" style="background:var(--bg-tertiary);color:var(--text-muted);font-size:0.875rem">Sin sincronizacion</span>`;
  }

  el.innerHTML = `
    <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px">${freshBadge}</div>
    <div class="banco-stats-grid">
      <div class="banco-stat-card">
        <div class="banco-stat-value">${total}</div>
        <div class="banco-stat-label">Registros sincronizados</div>
      </div>
      <div class="banco-stat-card">
        <div class="banco-stat-value" style="color:${synced.length > 0 ? 'var(--accent-green)' : 'var(--accent-yellow)'}">${synced.length}/${tables.length}</div>
        <div class="banco-stat-label">Tablas con datos</div>
      </div>
      <div class="banco-stat-card">
        <div class="banco-stat-value" style="font-size:0.9rem">${lastStr}</div>
        <div class="banco-stat-label">Ultimo sync</div>
      </div>
    </div>
    ${total === 0 ? '<div style="padding:12px;background:#0d2818;border:1px solid var(--accent-green);border-radius:6px;font-size:0.875rem;color:var(--text-secondary);margin-top:8px"><strong style="color:var(--accent-green-text)">Sin datos sincronizados.</strong> Ejecuta la sincronizacion desde Nexo Desktop: Config Desarrollo > Base de Datos > Sincronizar SQLite -> PostgreSQL.</div>' : ''}`;
}


/**
 * Router de pestanas -- CARGA DIFERIDA (SEP-INV-T2).
 *
 * Antes las 8 pestanas viajaban en el bundle principal aunque el usuario abriera una
 * sola. Ahora cada una llega en su propio pedazo, la primera vez que se abre. Separar
 * por separar NO habria servido: con import estatico Vite lo vuelve a juntar y el
 * navegador se lo baja igual (el manualChunks de vite.config es prueba viva de eso).
 *
 * Cada rama queda IGUAL que antes, con los mismos argumentos: es mudanza, no reforma.
 */
// Generacion del render de pestana. Mismo patron que ya usa verificacion.js para sus
// propios renders solapados: se toma un numero al entrar y, antes de pintar, se
// comprueba que siga siendo el mas nuevo. Si el usuario ya cambio de pestana mientras
// bajaba el pedazo, este render se retira sin pintar encima del siguiente.
let _tabGen = 0;

async function renderTab($el, tab, bizId) {
  const myGen = ++_tabGen;
  try {
    if (tab === 'productos') {
      const { renderSyncTable } = await import('./inventario/syncTable.js');
      if (myGen !== _tabGen) return;
      await renderSyncTable($el, 'productos', bizId);
    } else if (tab === 'insumos') {
      const { renderSyncTable } = await import('./inventario/syncTable.js');
      if (myGen !== _tabGen) return;
      await renderSyncTable($el, 'insumos', bizId);
    } else if (tab === 'matches') {
      // MATCH-ADMIN-T1: de que productos de proveedor llega cada insumo, y cambiarlo sin SQL (solo dueno/admin).
      const { renderMatches } = await import('./inventario/matches.js');
      if (myGen !== _tabGen) return;
      await renderMatches($el, bizId);
    } else if (tab === 'entrada') {
      const { renderEntradaInsumo } = await import('./inventario/entrada.js');
      if (myGen !== _tabGen) return;
      await renderEntradaInsumo($el, bizId);
    } else if (tab === 'configuracion') {
      const { renderConfiguracion } = await import('./inventario/configuracion.js');
      if (myGen !== _tabGen) return;
      await renderConfiguracion($el, bizId);
    } else if (tab === 'historial') {
      const { renderHistorialVerificaciones } = await import('./inventario/historial.js');
      if (myGen !== _tabGen) return;
      await renderHistorialVerificaciones($el, bizId);
    } else if (tab === 'avance') {
      const { renderAvanceGrupos } = await import('./inventario/avance.js');
      if (myGen !== _tabGen) return;
      await renderAvanceGrupos($el, bizId);
    } else if (tab === 'informacion') {
      await renderInformacion($el, bizId);
    } else {
      const { renderVerificacion } = await import('./inventario/verificacion.js');
      if (myGen !== _tabGen) return;
      await renderVerificacion($el, bizId);
    }
  } catch (err) {
    // Si el usuario ya se fue a otra pestana, este error no le importa: callarse es lo
    // correcto. Pintar aqui seria pisarle la pestana que si cargo.
    if (myGen !== _tabGen) return;
    mostrarPestanaCaida($el, tab, bizId, err);
  }
}

/**
 * La pestana no pudo cargar: DECIRLO. (H3, bitacora #9336 y #9554.)
 *
 * Con carga diferida, si el pedazo no baja -deploy nuevo que cambio los nombres, red
 * que se corta, cache raro- la promesa se rechaza y ANTES no la atrapaba nadie: el
 * usuario hacia clic y se quedaba el contenido de la pestana anterior, sin mensaje ni
 * error. Argy-Q lo reprodujo sin querer auditando el PR #255: hizo clic en Verificacion
 * y quedo Historial en pantalla. Eso es exactamente lo que veria un colaborador.
 */
function mostrarPestanaCaida($el, tab, bizId, err) {
  const sinRed = typeof navigator !== 'undefined' && navigator.onLine === false;
  const causa = sinRed
    ? 'Parece que te quedaste sin conexion.'
    : 'Puede que la aplicacion se haya actualizado hace un momento.';
  $el.innerHTML = `
    <div style="padding:16px;background:var(--bg-secondary);border:1px solid var(--accent-yellow);border-left:3px solid var(--accent-yellow);border-radius:6px;font-size:0.875rem;color:var(--accent-yellow)">
      <div style="font-weight:700;margin-bottom:6px">No pudimos cargar esta seccion</div>
      <div style="color:var(--text-secondary);line-height:1.5">${esc(causa)} Tu conteo no se perdio: no se envio nada.</div>
      <button id="nx-inv-tab-retry" class="btn btn-primary btn-sm" style="margin-top:10px;font-size:0.875rem">Reintentar</button>
      ${sinRed ? '' : '<button id="nx-inv-tab-reload" class="btn btn-ghost btn-sm" style="margin-top:10px;margin-left:8px;font-size:0.875rem">Recargar la pagina</button>'}
    </div>`;
  document.getElementById('nx-inv-tab-retry')?.addEventListener('click', () => renderTab($el, tab, bizId));
  document.getElementById('nx-inv-tab-reload')?.addEventListener('click', () => window.location.reload());
  console.error('[inventario] la pestana', tab, 'no cargo:', err);
}


/**
 * Tab "Informacion" - stats de sync movidas aqui para compactar el mobile.
 * Muestra los 3 cards (registros, tablas con datos, ultimo sync) + chip frescura
 * que antes estaban arriba de TODO el componente.
 */
async function renderInformacion($el, bizId) {
  $el.innerHTML = `
    <div class="detail-section">
      <div style="margin-bottom:10px">
        <h2 style="margin:0;font-size:1rem">Informacion de sincronizacion</h2>
        <p style="font-size:0.875rem;color:var(--text-secondary);margin-top:2px">
          Estado tecnico de la sincronizacion desde Nexo Desktop.
        </p>
      </div>
      <div id="nx-inv-sync-status"></div>
    </div>`;
  await loadSyncStatus(bizId);
}



// Nota: el drenado global al volver online esta centralizado en CarritoView
// (OfflineQueue.flush drena TODAS las operaciones — carrito e inventario —
// en una sola pasada). No duplicar aqui.

/**
 * FIX #2050 (task 3f144f56): la cola offline NUNCA drenaba desde #/inventario --
 * OfflineQueue.flush solo se invocaba en CarritoView, cuyo listener 'online'
 * ademas solo existe si ese modulo se cargo (lazy por router). Si el operador
 * solo usa el kiosk inventario, los conteos encolados quedaban pendientes para
 * siempre con conexion sana, y el banner era informativo sin accion.
 *
 * Politica de antiguedad (spec): un conteo de inventario de OTRO dia NO se
 * reenvia a ciegas (pisaria el consolidado del dia equivocado). Auto-drenan:
 * ops de carrito (una venta vieja sigue valida) + inventario DE HOY. Los
 * antiguos quedan listados en el banner con dos acciones explicitas por item:
 * "Enviar igual" (confirmacion 2 pasos) o "Descartar".
 */
const _esInventarioAntiguo = (op) => {
  if (op.tipo !== 'inventario') return false;
  const d = new Date(op.created_at || 0);
  return isNaN(d.getTime()) ? true : formatearFecha(d) !== formatearFecha(new Date());
};



const _offlineSender = async ({ endpoint, payload }) => {
  try {
    const res = await ApiBase.post(endpoint, payload);
    return { ok: !!res.ok, error: res.error };
  } catch (err) {
    return { ok: false, error: err?.message || 'network' };
  }
};



async function flushInventarioPendientes() {
  if (!OfflineQueue.isOnline()) return { sent: 0, failed: 0, remaining: 0 };
  // Drena carrito + inventario-de-hoy; los antiguos NO pasan el filtro.
  const result = await OfflineQueue.flush(_offlineSender, undefined, { filter: (op) => !_esInventarioAntiguo(op) });
  renderInvOfflineBanner();
  return result;
}

// Drenado al recuperar conexion, registrado UNA vez por carga del modulo
// (mismo patron CarritoView, pero ahora el kiosk inventario no depende de
// que aquel modulo se haya cargado).
window.addEventListener('online', () => { flushInventarioPendientes().catch(() => {}); });



async function renderInvOfflineBanner() {
  const $el = document.getElementById('nx-inv-offline-banner');
  if (!$el) return;
  const online = OfflineQueue.isOnline();
  const pending = await OfflineQueue.listPending();
  const items = Array.isArray(pending) ? pending : [];
  const antiguos = items.filter(_esInventarioAntiguo);
  const drenables = items.length - antiguos.length;
  if (online && items.length === 0) { $el.innerHTML = ''; return; }
  if (!online) {
    $el.innerHTML = `
      <div class="ops-offline-banner" data-state="offline">
        <strong>Sin conexion.</strong> Los datos mostrados pueden ser de cache. Los envios se encolan.
        ${items.length > 0 ? ` <span style="opacity:0.85">(${items.length} pendiente${items.length === 1 ? '' : 's'})</span>` : ''}
      </div>`;
    return;
  }
  const filaAntiguo = (op) => {
    const fecha = op.created_at ? formatearFecha(op.created_at) : 'fecha desconocida';
    return `
      <div data-op-id="${op.id}" style="display:flex;gap:8px;align-items:center;padding:4px 0;font-size:0.875rem;flex-wrap:wrap">
        <span style="color:var(--accent-yellow)">⚠ Conteo del <strong>${fecha}</strong></span>
        <span style="color:var(--text-secondary)">(${op.attempts || 0} intento${(op.attempts || 0) === 1 ? '' : 's'})</span>
        <button type="button" class="btn-ghost btn-sm nx-off-enviar" data-confirm="0" style="font-size:0.875rem;padding:2px 8px" title="Reenviar este conteo viejo: puede pisar el consolidado de ese dia en el historial">Enviar igual</button>
        <button type="button" class="btn-ghost btn-sm nx-off-descartar" data-confirm="0" style="font-size:0.875rem;padding:2px 8px;color:var(--accent-red)" title="Eliminar de la cola sin enviar">Descartar</button>
      </div>`;
  };
  $el.innerHTML = `
    <div class="ops-offline-banner" data-state="pending">
      <strong>${items.length} operacion${items.length === 1 ? '' : 'es'} pendiente${items.length === 1 ? '' : 's'} de sincronizar.</strong>
      ${drenables > 0 ? `<button type="button" id="nx-off-flush" class="btn-primary btn-sm" style="margin-left:8px;font-size:0.875rem;padding:3px 10px">Sincronizar ahora (${drenables})</button>` : ''}
      ${antiguos.length > 0 ? `
        <div style="margin-top:6px;padding-top:6px;border-top:1px dashed #d2992255">
          <span style="font-size:0.875rem;color:var(--accent-yellow)">${antiguos.length} conteo${antiguos.length === 1 ? '' : 's'} de OTRO dia — no se reenvian automatico para no pisar el consolidado de ese dia:</span>
          ${antiguos.map(filaAntiguo).join('')}
        </div>` : ''}
    </div>`;
  bindInvOfflineBannerDelegation($el);
}

/** Delegacion en el host estable del banner (leccion R-T6: inmune a re-renders). */


/** Delegacion en el host estable del banner (leccion R-T6: inmune a re-renders). */
function bindInvOfflineBannerDelegation($el) {
  if ($el.dataset.nxDelegated === '1') return;
  $el.dataset.nxDelegated = '1';
  $el.addEventListener('click', async (e) => {
    if (e.target.closest('#nx-off-flush')) {
      e.target.disabled = true;
      e.target.textContent = 'Sincronizando…';
      await flushInventarioPendientes();
      return;
    }
    const btnEnviar = e.target.closest('.nx-off-enviar');
    if (btnEnviar) {
      if (btnEnviar.dataset.confirm !== '1') {
        btnEnviar.dataset.confirm = '1';
        btnEnviar.textContent = '¿Seguro? Pisa ese dia';
        btnEnviar.style.color = 'var(--text-on-accent)';
        btnEnviar.style.background = 'var(--accent-yellow)';
        setTimeout(() => {
          btnEnviar.dataset.confirm = '0';
          btnEnviar.textContent = 'Enviar igual';
          btnEnviar.style.color = '';
          btnEnviar.style.background = '';
        }, 3500);
        return;
      }
      const opId = Number(btnEnviar.closest('[data-op-id]')?.dataset.opId);
      btnEnviar.disabled = true;
      btnEnviar.textContent = 'Enviando…';
      const pendientes = await OfflineQueue.listPending();
      const op = pendientes.find(p => p.id === opId);
      if (op) {
        const res = await _offlineSender({ endpoint: op.endpoint, payload: op.payload });
        if (res.ok) await OfflineQueue.remove(opId);
      }
      renderInvOfflineBanner();
      return;
    }
    const btnDesc = e.target.closest('.nx-off-descartar');
    if (btnDesc) {
      if (btnDesc.dataset.confirm !== '1') {
        btnDesc.dataset.confirm = '1';
        btnDesc.textContent = '¿Confirmar descarte?';
        btnDesc.style.background = 'var(--accent-red)';
        btnDesc.style.color = 'var(--text-on-accent)';
        setTimeout(() => {
          btnDesc.dataset.confirm = '0';
          btnDesc.textContent = 'Descartar';
          btnDesc.style.background = '';
          btnDesc.style.color = 'var(--accent-red)';
        }, 3500);
        return;
      }
      const opId = Number(btnDesc.closest('[data-op-id]')?.dataset.opId);
      await OfflineQueue.remove(opId);
      renderInvOfflineBanner();
    }
  });
}

/**
 * Bloqueo grande cuando un negocio intenta entrar a Inventario sin operador identificado.
 * Se levanta el modal de PIN al presionar el CTA (mismo handler que el chip "Identificar
 * operador" del navbar: click forwardado a #nx-op-badge/#nx-op-identify).
 */


/**
 * Bloqueo grande cuando un negocio intenta entrar a Inventario sin operador identificado.
 * Se levanta el modal de PIN al presionar el CTA (mismo handler que el chip "Identificar
 * operador" del navbar: click forwardado a #nx-op-badge/#nx-op-identify).
 */
function renderBloqueoOperador(container) {
  container.innerHTML = `
    <section class="business-require-auth">
      <div class="require-auth-card">
        <div class="require-auth-icon">&#128274;</div>
        <h2>Identificate para continuar</h2>
        <p>Por seguridad, debes ingresar tu PIN de operador antes de usar el inventario.</p>
        <button type="button" id="nx-inv-auth-cta" class="btn-primary">Identificarse</button>
      </div>
    </section>`;
  document.getElementById('nx-inv-auth-cta')?.addEventListener('click', () => {
    const kioskBtn = document.getElementById('nx-op-badge') || document.getElementById('nx-op-identify');
    if (kioskBtn) kioskBtn.click();
  });
}

